import { useEffect, useState } from 'react';
import { AlertCircle, CheckCircle, ChevronDown, Clock, Users, MessageCircle, Zap, Edit2, Check, X } from 'react-feather';
import { serviceTestingAPI, adminAPI } from '../api';
import './AdminServiceTesting.css';

export default function AdminServiceTesting() {
  const [services, setServices] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [selectedTesters, setSelectedTesters] = useState({});
  const [feedback, setFeedback] = useState({});
  const [msg, setMsg] = useState('');
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [servicesRes, usersRes] = await Promise.all([
        serviceTestingAPI.adminTesting(),
        adminAPI.users(),
      ]);
      setServices(servicesRes.data.services || []);
      setUsers(usersRes.data.users || []);
    } catch (err) {
      setMsg(err.response?.data?.error || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadDetail = async (serviceId) => {
    try {
      const res = await serviceTestingAPI.adminTestingDetail(serviceId);
      setFeedback(prev => ({
        ...prev,
        [serviceId]: res.data.feedback || [],
      }));
    } catch (err) {
      console.error('Failed to load feedback:', err);
    }
  };

  const toggleExpand = (serviceId) => {
    if (expandedId === serviceId) {
      setExpandedId(null);
    } else {
      setExpandedId(serviceId);
      loadDetail(serviceId);
    }
  };

  const handleTesterSelect = (serviceId, userId) => {
    setSelectedTesters(prev => {
      const current = prev[serviceId] || [];
      const updated = current.includes(userId)
        ? current.filter(id => id !== userId)
        : [...current, userId];
      return { ...prev, [serviceId]: updated };
    });
  };

  const assignTesters = async (serviceId) => {
    const tester_ids = selectedTesters[serviceId] || [];
    if (!tester_ids.length) {
      setMsg('Select at least one tester');
      return;
    }

    setSavingId(serviceId);
    setMsg('');
    try {
      await serviceTestingAPI.assignTesters(serviceId, tester_ids);
      setMsg(`Assigned ${tester_ids.length} testers to service`);
      setSelectedTesters(prev => ({ ...prev, [serviceId]: [] }));
      await loadData();
    } catch (err) {
      setMsg(err.response?.data?.error || 'Failed to assign testers');
    } finally {
      setSavingId(null);
    }
  };

  const approveService = async (serviceId) => {
    setSavingId(serviceId);
    setMsg('');
    try {
      await serviceTestingAPI.approve(serviceId);
      setMsg('Service approved and activated!');
      await loadData();
    } catch (err) {
      setMsg(err.response?.data?.error || 'Failed to approve service');
    } finally {
      setSavingId(null);
    }
  };

  const rejectService = async (serviceId) => {
    const reason = prompt('Enter rejection reason (optional):');
    setSavingId(serviceId);
    setMsg('');
    try {
      await serviceTestingAPI.reject(serviceId, reason);
      setMsg('Service rejected and returned to draft');
      await loadData();
    } catch (err) {
      setMsg(err.response?.data?.error || 'Failed to reject service');
    } finally {
      setSavingId(null);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'badge badge-warning',
      testing: 'badge badge-info',
      active: 'badge badge-success',
    };
    return <span className={styles[status]}>{status.toUpperCase()}</span>;
  };

  const getFeedbackColor = (status) => {
    switch (status) {
      case 'working': return '#10b981';
      case 'issue': return '#f59e0b';
      case 'not_working': return '#ef4444';
      default: return '#6b7280';
    }
  };

  if (loading) {
    return (
      <div>
        <h1 className="page-title">Service Testing</h1>
        <div className="loading-screen"><div className="spinner" /></div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="page-title">Service Testing Management</h1>
      <p className="page-subtitle">Approve new services before making them available to all users</p>

      {msg && (
        <div className={`alert ${msg.includes('Failed') ? 'alert-error' : 'alert-success'}`}>
          <CheckCircle size={18} /> <span>{msg}</span>
        </div>
      )}

      <div className="service-testing-list">
        {services.length ? (
          services.map(service => {
            const testers = selectedTesters[service.id] || [];
            const serviceFeedback = feedback[service.id] || [];
            return (
              <div className="service-testing-card" key={service.id}>
                <div className="service-testing-header" onClick={() => toggleExpand(service.id)}>
                  <div className="service-testing-info">
                    <h3>{service.name}</h3>
                    <div className="service-testing-meta">
                      <span>{service.category}</span>
                      {getStatusBadge(service.approval_status)}
                    </div>
                  </div>
                  <ChevronDown size={20} className={expandedId === service.id ? 'rotated' : ''} />
                </div>

                {expandedId === service.id && (
                  <div className="service-testing-details">
                    <div className="detail-row">
                      <span className="label">Created by:</span>
                      <span>{service.created_by_username || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="label">Min/Max:</span>
                      <span>{service.min} - {service.max}</span>
                    </div>

                    {service.approval_status === 'draft' && (
                      <div className="tester-selection">
                        <h4>Assign Testers</h4>
                        <div className="tester-checkboxes">
                          {users.map(user => (
                            <label key={user.id} className="checkbox-label">
                              <input
                                type="checkbox"
                                checked={testers.includes(user.id)}
                                onChange={() => handleTesterSelect(service.id, user.id)}
                                disabled={savingId === service.id}
                              />
                              <span>{user.username} ({user.email})</span>
                            </label>
                          ))}
                        </div>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => assignTesters(service.id)}
                          disabled={savingId === service.id || !testers.length}
                        >
                          {savingId === service.id ? 'Assigning...' : 'Assign Testers'}
                        </button>
                      </div>
                    )}

                    {service.tested_by && service.tested_by.length > 0 && (
                      <div className="testers-assigned">
                        <h4>Assigned Testers ({service.tested_by.length})</h4>
                        <div className="testers-list">
                          {service.tested_by.map((userId, idx) => {
                            const user = users.find(u => u.id === userId);
                            return <span key={idx} className="tester-badge">{user?.username || `User ${userId}`}</span>;
                          })}
                        </div>
                      </div>
                    )}

                    {serviceFeedback.length > 0 && (
                      <div className="feedback-section">
                        <h4>Tester Feedback ({serviceFeedback.length})</h4>
                        {serviceFeedback.map(fb => (
                          <div key={fb.id} className="feedback-item">
                            <div className="feedback-header">
                              <strong>{fb.username}</strong>
                              <span className="feedback-status" style={{ backgroundColor: getFeedbackColor(fb.status) }}>
                                {fb.status.toUpperCase()}
                              </span>
                            </div>
                            {fb.feedback && <p>{fb.feedback}</p>}
                            <small>{new Date(fb.submitted_at).toLocaleString()}</small>
                          </div>
                        ))}
                      </div>
                    )}

                    {service.approval_status === 'testing' && (
                      <div className="approval-actions">
                        <button
                          className="btn btn-success btn-sm"
                          onClick={() => approveService(service.id)}
                          disabled={savingId === service.id}
                        >
                          <Check size={14} /> Approve
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => rejectService(service.id)}
                          disabled={savingId === service.id}
                        >
                          <X size={14} /> Reject
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <p className="empty-state">No services in draft or testing status</p>
        )}
      </div>
    </div>
  );
}
